import type {
  MedicineWithoutPrice,
  ReceiptPreviewItemParams,
  ReceiptPreviewResponse,
} from '@/api';

import type { IntakeMethod } from './medicine-fields';
import type { OrderMedicine } from './order-medicine';

/** Строка «Итога» по одной карточке препарата. */
export type OrderPreviewRow = {
  /** Цена с бэка; null — цену не прислали (нет в справочнике цен). */
  price: number | null;
  /** Сколько упаковок нужно купить; null, если препарат в нескольких карточках. */
  packages: number | null;
  packSize: number | null;
};

export type OrderPreview = {
  /** По индексам wizardState.additionalMedicines. */
  rows: OrderPreviewRow[];
  medicinesTotal: number;
  deliveryPrice: number;
  packageFee: number;
  vatAmount: number;
  total: number;
  medicinesWithoutPrice: MedicineWithoutPrice[];
};

const toNumber = (value?: string | number | null): number => {
  const parsed =
    typeof value === 'number' ? value : Number.parseFloat(value ?? '');
  return Number.isFinite(parsed) ? parsed : 0;
};

const round2 = (value: number): number => Math.round(value * 100) / 100;

/** Сколько таблеток уходит по карточке — вес для распределения цены. */
const cardWeight = (medicine: OrderMedicine): number => {
  const perDay = medicine.time.reduce(
    (sum, time) => sum + (time.count || 0),
    0,
  );
  return perDay * (medicine.duration || 0) || medicine.totalCount || 1;
};

/**
 * Позиции для расчёта — ровно те же, что уйдут в receipt_items при создании
 * заказа (один элемент на приём), иначе цена в «Итоге» разойдётся с заказом.
 * Карточки без препарата или без частоты пропускаем: бэкенд ответит на них 400.
 */
export const buildPreviewItems = (
  medicines: OrderMedicine[],
  resolveEatingId: (method?: IntakeMethod) => number | undefined,
): ReceiptPreviewItemParams[] =>
  medicines
    .filter((medicine) => medicine.medicine?.id && medicine.frequency?.id)
    .flatMap((medicine) =>
      medicine.time.map((time) => ({
        medicine_id: medicine.medicine.id,
        amount: Number(time.count),
        duration: medicine.duration,
        frequency_id: medicine.frequency.id,
        start: medicine.start,
        daytime_id: time.timeId,
        eating_id: time.eatingId ?? resolveEatingId(medicine.intakeMethod),
      })),
    );

/**
 * Ответ приходит по medicine_id и без привязки к карточке, а один и тот же
 * препарат может стоять в нескольких карточках с разной схемой приёма.
 * Поэтому суммируем цену по препарату и делим её между его карточками
 * пропорционально количеству таблеток; в обычном случае карточка одна и
 * цена совпадает с присланной. Остаток отдаём последней карточке, чтобы
 * сумма строк сходилась с итогом по позициям. Итог заказа берём из
 * total_price бэкенда (как на детализации заказа).
 */
export const mapPreviewToOrderPreview = (
  medicines: OrderMedicine[],
  response: ReceiptPreviewResponse,
): OrderPreview => {
  const totals = new Map<
    number,
    { price: number; packages: number; packSize: number | null }
  >();

  for (const item of response.order_items) {
    const acc = totals.get(item.medicine_id) ?? {
      price: 0,
      packages: 0,
      packSize: null,
    };
    acc.price = round2(acc.price + toNumber(item.price));
    acc.packages += Number(item.amount) || 0;
    acc.packSize = acc.packSize ?? (Number(item.pack_size) || null);
    totals.set(item.medicine_id, acc);
  }

  const cardsByMedicine = new Map<number, number[]>();
  medicines.forEach((medicine, index) => {
    const cards = cardsByMedicine.get(medicine.medicine?.id) ?? [];
    cards.push(index);
    cardsByMedicine.set(medicine.medicine?.id, cards);
  });

  const rows: OrderPreviewRow[] = medicines.map(() => ({
    price: null,
    packages: null,
    packSize: null,
  }));

  cardsByMedicine.forEach((cards, medicineId) => {
    const acc = totals.get(medicineId);
    if (!acc) return;

    const weights = cards.map((cardIndex) => cardWeight(medicines[cardIndex]));
    const weightSum = weights.reduce((sum, weight) => sum + weight, 0);
    let distributed = 0;

    cards.forEach((cardIndex, i) => {
      const isLast = i === cards.length - 1;
      const price = isLast
        ? round2(acc.price - distributed)
        : round2((acc.price * weights[i]) / (weightSum || cards.length));
      distributed = round2(distributed + price);

      rows[cardIndex] = {
        price,
        packages: cards.length === 1 ? acc.packages || null : null,
        packSize: cards.length === 1 ? acc.packSize : null,
      };
    });
  });

  return {
    rows,
    medicinesTotal: toNumber(response.price),
    deliveryPrice: toNumber(response.delivery_price),
    packageFee: toNumber(response.package_fee),
    vatAmount: toNumber(response.vat_amount),
    total: toNumber(response.total_price),
    medicinesWithoutPrice: response.medicines_without_price,
  };
};
